import { FC, FunctionComponent, ReactNode, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { getFrontLogger } from 'utils/loggers/FrontLogger';
import type { Streaming } from 'utils/streaming/model';

import { handlePriceImprovementEvent } from './notifications.helper';

export function withNotifications<P extends object>(
  Component: FunctionComponent<P>,
  streaming: Streaming,
): FunctionComponent<P> {
  return (props: P) => (
    <NotificationsProvider streaming={streaming}>
      <Component {...props} />
    </NotificationsProvider>
  );
}

export const NotificationsProvider: FC<{
  children: ReactNode;
  streaming: Streaming;
}> = ({ streaming: { priceImprovementMessage$ }, children }) => {
  const intl = useIntl();

  useEffect(() => {
    const frontLogger = getFrontLogger();

    if (!('Notification' in window)) {
      frontLogger.info('user browser does not handle notification');
    }
    // Let's check whether notification permissions have already been granted
    // Otherwise, we need to ask the user for permission
    else if (Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        frontLogger.info('user notifications choice {permission}', permission);
      });
    } else {
      frontLogger.info('user notifications choice {permission}', Notification.permission);
    }
    const subscription = priceImprovementMessage$.subscribe(handlePriceImprovementEvent(intl));

    return function cleanup() {
      if (!subscription.closed) {
        subscription.unsubscribe();
      }
    };
  }, [intl, priceImprovementMessage$]);

  return <>{children}</>;
};

NotificationsProvider.displayName = 'NotificationsProvider';
